import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import bcrypt from 'bcryptjs';
import './StudentProfileSettings.css';
import { supabase, signOut } from '../../utils/supabaseClient';
import { uploadFile } from '../../utils/storageService';
import { normalizeTimezone } from '../../utils/timezone';
import { usePaymentBalance } from '../../hooks/usePaymentBalance';
import { languages, nativeLanguages, countries, timezones } from '../../data/profileOptions';
import { NavIcon } from '../ProfileSettingsIcons';
import ConfirmSignOutModal from '../ConfirmSignOutModal';

// Interface language is saved as profile metadata but doesn't actually
// translate the UI yet (no i18n system in the project) — restricted to
// English only so the picker doesn't imply a working language switch.
const INTERFACE_LANGUAGES = languages.filter(l => l.value === 'english');

const StudentProfileSettings = ({ studentData, initialTab }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialTab || 'personal-info');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

  // Handles navigating here while already mounted (e.g. clicking "Payments"
  // in the sidebar user menu while sitting on Profile Settings already) —
  // the useState initializer above only covers a fresh mount.
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  // This tab doesn't fetch lessons/schedule itself, so it can't run the
  // auto-charge reconciliation (ready: false) — it only needs to display
  // whatever's already reconciled (by HomeStudent or the teacher's
  // StudentProfile), reading through the same localStorage ledger instead
  // of the raw, possibly-stale studentData.payments field directly.
  const { payments: reconciledPayments, paymentBalance, paymentCurrency } = usePaymentBalance({
    studentId: studentData?.slug,
    studentSupabaseId: studentData?.id,
    studentData,
    lessons: [],
    scheduleItems: [],
    ready: false,
  });

  // Sign-out from the profile screen — the student sidebar is hidden
  // on mobile, so without a button here mobile users had no way to
  // log out.
  const handleSignOut = async () => {
    setShowSignOutConfirm(false);
    try { await signOut(); } catch (e) { console.warn('signOut failed:', e); }
    try {
      localStorage.removeItem('currentUser');
      localStorage.removeItem('isAuthenticated');
    } catch (_) {}
    navigate('/login');
  };
  const [savedToast, setSavedToast] = useState(false);
  const savedToastTimeoutRef = React.useRef(null);
  const [showDeviceSettings, setShowDeviceSettings] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [selectedMicrophone, setSelectedMicrophone] = useState('');
  const [availableCameras, setAvailableCameras] = useState([]);
  const [availableMicrophones, setAvailableMicrophones] = useState([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [microphoneLevel, setMicrophoneLevel] = useState(0);
  const videoRef = React.useRef(null);
  const audioContextRef = React.useRef(null);
  const analyserRef = React.useRef(null);
  const microphoneStreamRef = React.useRef(null);
  
  const showSavedToast = () => {
    if (savedToastTimeoutRef.current) clearTimeout(savedToastTimeoutRef.current);
    setSavedToast(true);
    savedToastTimeoutRef.current = setTimeout(() => {
      setSavedToast(false);
      savedToastTimeoutRef.current = null;
    }, 3000);
  };

  // Get student data from localStorage
  const getCurrentStudent = () => {
    try {
      console.log('Getting current student data...');
      console.log('studentData prop:', studentData);
      
      const allStudentDataRaw = localStorage.getItem('novas_students_data');
      console.log('Raw allStudentData from localStorage:', allStudentDataRaw);
      
      const allStudentData = JSON.parse(allStudentDataRaw || '{}');
      console.log('Parsed allStudentData:', allStudentData);
      console.log('All student data keys:', Object.keys(allStudentData));
      
      const studentSlug = studentData?.slug || studentData?.originalSlug || studentData?.id;
      
      // Проверяем, есть ли студент с таким slug
      if (studentSlug && !allStudentData[studentSlug]) {
        console.log('Student not found by slug, trying to find by other means...');
        console.log('Available slugs:', Object.keys(allStudentData));
        console.log('Looking for student with email:', studentData?.email);
        
        // Попробуем найти студента по email из userAccount
        const studentByEmail = Object.entries(allStudentData).find(([key, data]) => 
          data.userAccount && data.userAccount.email === studentData?.email
        );
        if (studentByEmail) {
          console.log('Found student by email:', studentByEmail[0]);
          const [foundSlug] = studentByEmail;
          const studentEmail = allStudentData[foundSlug].isRegistered && allStudentData[foundSlug].userAccount 
            ? allStudentData[foundSlug].userAccount.email 
            : allStudentData[foundSlug].contactEmail || '';
            
          const studentPhone = allStudentData[foundSlug].isRegistered && allStudentData[foundSlug].userAccount 
            ? allStudentData[foundSlug].userAccount.phone || ''
            : allStudentData[foundSlug].contactPhone || '';
            
          return {
            name: allStudentData[foundSlug].name || 'Student',
            email: studentEmail,
            phone: studentPhone
          };
        } else {
          console.log('Student not found by email either');
        }
      }
      
      if (studentSlug && allStudentData[studentSlug]) {
        console.log('Found student in localStorage:', allStudentData[studentSlug]);
        console.log('Student isRegistered:', allStudentData[studentSlug].isRegistered);
        console.log('Student userAccount:', allStudentData[studentSlug].userAccount);
        
        // Если студент зарегистрирован, показываем email из userAccount
        const studentEmail = allStudentData[studentSlug].isRegistered && allStudentData[studentSlug].userAccount 
          ? allStudentData[studentSlug].userAccount.email 
          : allStudentData[studentSlug].contactEmail || '';
          
        // Получаем телефон из userAccount.phone если студент зарегистрирован
        const studentPhone = allStudentData[studentSlug].isRegistered && allStudentData[studentSlug].userAccount 
          ? allStudentData[studentSlug].userAccount.phone || ''
          : allStudentData[studentSlug].contactPhone || '';
          
        console.log('Student email:', studentEmail);
        console.log('Student phone from userAccount:', allStudentData[studentSlug].userAccount?.phone);
        console.log('Student phone from contactPhone:', allStudentData[studentSlug].contactPhone);
        console.log('Final student phone:', studentPhone);
          
        return {
          name: allStudentData[studentSlug].name || 'Student',
          email: studentEmail,
          phone: studentPhone,
          photo: allStudentData[studentSlug].photo || null,
        };
      }
      
      // Fallback для случая, когда studentData передается как prop
      console.log('Using fallback data from prop');
      const studentEmail = studentData?.isRegistered && studentData?.userAccount 
        ? studentData.userAccount.email 
        : studentData?.contactEmail || '';
        
      const studentPhone = studentData?.isRegistered && studentData?.userAccount 
        ? studentData.userAccount.phone || ''
        : studentData?.contactPhone || '';
        
      return {
        name: studentData?.name || '',
        email: studentEmail,
        phone: studentPhone,
        photo: studentData?.photo || null,
      };
    } catch (error) {
      console.error('Error getting student data:', error);
      return {
        name: '',
        email: '',
        phone: ''
      };
    }
  };
  
  const [profileData, setProfileData] = useState(getCurrentStudent());
  const [originalData, setOriginalData] = useState(getCurrentStudent());
  const [hasNameChanges, setHasNameChanges] = useState(false);
  const [hasPhoneChanges, setHasPhoneChanges] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [languageSearch, setLanguageSearch] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState({ flag: '🇬🇧', name: 'English', value: 'english' });
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [nativeLanguageSearch, setNativeLanguageSearch] = useState('');
  const [selectedNativeLanguage, setSelectedNativeLanguage] = useState({ flag: '🇬🇧', name: 'English', value: 'english' });
  const [showNativeLanguageDropdown, setShowNativeLanguageDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState({ flag: '🇦🇲', name: 'Armenia', value: 'armenia' });
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [timezoneSearch, setTimezoneSearch] = useState('');
  const [selectedTimezone, setSelectedTimezone] = useState({ name: 'Yerevan (UTC+4)', value: 'Asia/Yerevan' });
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false);
  const [telegramEnabled, setTelegramEnabled] = useState(true);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const fileInputRef = React.useRef(null);

  // Load student data on component mount
  useEffect(() => {
    const studentInfo = getCurrentStudent();
    setProfileData(studentInfo);
    setOriginalData(studentInfo);
    setProfilePhoto(studentInfo.photo || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentData]);

  // Listen for student data updates
  useEffect(() => {
    const handleStudentDataUpdate = () => {
      console.log('Student data updated, reloading...');
      const studentInfo = getCurrentStudent();
      setProfileData(studentInfo);
      setOriginalData(studentInfo);
      setProfilePhoto(studentInfo.photo || null);
    };

    window.addEventListener('studentDataUpdated', handleStudentDataUpdate);
    
    return () => {
      window.removeEventListener('studentDataUpdated', handleStudentDataUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentData]);

  // Reload data when component mounts (when returning to settings)
  useEffect(() => {
    console.log('Component mounted, reloading student data...');
    console.log('Current studentData prop:', studentData);
    const studentInfo = getCurrentStudent();
    console.log('Loaded student info:', studentInfo);
    setProfileData(studentInfo);
    setOriginalData(studentInfo);
    setProfilePhoto(studentInfo.photo || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredLanguages = INTERFACE_LANGUAGES.filter(lang =>
    lang.name.toLowerCase().includes(languageSearch.toLowerCase())
  );

  const filteredNativeLanguages = nativeLanguages.filter(lang => 
    lang.name.toLowerCase().includes(nativeLanguageSearch.toLowerCase())
  );

  const filteredCountries = countries.filter(country => 
    country.name.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const filteredTimezones = timezones.filter(timezone => 
    timezone.name.toLowerCase().includes(timezoneSearch.toLowerCase())
  );

  // Init timezone from Supabase-backed studentData if present (legacy values supported)
  useEffect(() => {
    const tz = studentData?.timezone;
    if (!tz) return;
    const found = timezones.find(t => t.value === tz) || timezones.find(t => normalizeTimezone(t.value) === normalizeTimezone(tz));
    if (found) setSelectedTimezone(found);
  }, [studentData?.timezone]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const lang = studentData?.interface_language;
    if (!lang) return;
    const found = INTERFACE_LANGUAGES.find(l => l.value === lang);
    if (found) setSelectedLanguage(found);
  }, [studentData?.interface_language]);

  useEffect(() => {
    const lang = studentData?.native_language;
    if (!lang) return;
    const found = nativeLanguages.find(l => l.value === lang);
    if (found) setSelectedNativeLanguage(found);
  }, [studentData?.native_language]);

  useEffect(() => {
    const country = studentData?.country;
    if (!country) return;
    const found = countries.find(c => c.value === country);
    if (found) setSelectedCountry(found);
  }, [studentData?.country]);

  useEffect(() => {
    setTelegramEnabled(studentData?.telegram_notifications_enabled !== false);
  }, [studentData?.telegram_notifications_enabled]);

  // Mirrors the teacher's useTelegramNotifications.js — gates teacher->student
  // forwarding in supabase/functions/telegram-webhook/index.ts. Optimistic
  // toggle with revert-on-error, same as the teacher's hook.
  const toggleTelegramNotifications = async () => {
    const next = !telegramEnabled;
    setTelegramEnabled(next);
    setTelegramLoading(true);
    try {
      const studentId = studentData?.id || studentData?.studentId;
      if (!studentId) throw new Error('Student not identified');
      const { error } = await supabase
        .from('students')
        .update({ telegram_notifications_enabled: next, updated_at: new Date().toISOString() })
        .eq('id', studentId);
      if (error) throw error;
      showSavedToast();
    } catch (e) {
      console.warn('StudentProfileSettings - failed to save telegram_notifications_enabled:', e);
      setTelegramEnabled(!next);
    } finally {
      setTelegramLoading(false);
    }
  };

  // Shared by timezone/interface_language/native_language/country — all
  // "select one option, save immediately" fields. Writes straight to
  // Supabase by studentId (mirrors the teacher's saveTeacherField in
  // ProfileSettings.jsx), plus keeps the legacy novas_students_data
  // localStorage cache in sync via saveStudentField since some dashboard
  // widgets still read from there first.
  const saveStudentSupabaseField = async (field, value) => {
    saveStudentField(field, value);
    try {
      const studentId = studentData?.id || studentData?.studentId;
      if (studentId) {
        const { error } = await supabase
          .from('students')
          .update({ [field]: value, updated_at: new Date().toISOString() })
          .eq('id', studentId);
        if (error) console.warn(`StudentProfileSettings - failed to save ${field} to Supabase:`, error);
      }
      window.dispatchEvent(new CustomEvent('studentDataUpdated'));
    } catch (e) {
      console.warn(`StudentProfileSettings - ${field} save exception:`, e);
    }
    showSavedToast();
  };

  const saveStudentTimezone = (tzValue) => saveStudentSupabaseField('timezone', tzValue);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.student-profile-language-dropdown')) {
        setShowLanguageDropdown(false);
        setShowNativeLanguageDropdown(false);
        setShowCountryDropdown(false);
        setShowTimezoneDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const openDeviceSettings = () => setShowDeviceSettings(true);

  const closeDeviceSettings = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    if (microphoneStreamRef.current) {
      microphoneStreamRef.current.getTracks().forEach(track => track.stop());
      microphoneStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    setMicrophoneLevel(0);
    setShowDeviceSettings(false);
  };

  const saveDeviceSettings = () => {
    localStorage.setItem('selectedCamera', selectedCamera);
    localStorage.setItem('selectedMicrophone', selectedMicrophone);
    closeDeviceSettings();
    showSavedToast();
  };

  const startCamera = async (deviceId) => {
    try {
      setCameraError(null);
      if (cameraStream) cameraStream.getTracks().forEach(track => track.stop());
      const constraints = { video: deviceId ? { deviceId: { exact: deviceId } } : true, audio: false };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setCameraStream(stream);
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (error) {
      console.error('Error starting camera:', error);
      setCameraError(error.message);
    }
  };

  const startMicrophone = async (deviceId) => {
    try {
      if (microphoneStreamRef.current) microphoneStreamRef.current.getTracks().forEach(track => track.stop());
      const constraints = { audio: deviceId ? { deviceId: { exact: deviceId } } : true, video: false };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      microphoneStreamRef.current = stream;
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      analyser.fftSize = 256;
      source.connect(analyser);
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateLevel = () => {
        analyser.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
        setMicrophoneLevel(Math.round(((sum / dataArray.length) / 255) * 100));
        if (microphoneStreamRef.current?.active) requestAnimationFrame(updateLevel);
      };
      updateLevel();
    } catch (error) {
      console.error('Error starting microphone:', error);
    }
  };

  const getDevices = async () => {
    setIsLoadingDevices(true);
    try {
      await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(d => d.kind === 'videoinput');
      const mics = devices.filter(d => d.kind === 'audioinput');
      setAvailableCameras(cameras);
      setAvailableMicrophones(mics);
      if (cameras.length > 0) {
        if (!selectedCamera) setSelectedCamera(cameras[0].deviceId);
        setTimeout(() => startCamera(selectedCamera || cameras[0].deviceId), 100);
      }
      if (mics.length > 0) {
        if (!selectedMicrophone) setSelectedMicrophone(mics[0].deviceId);
        setTimeout(() => startMicrophone(selectedMicrophone || mics[0].deviceId), 200);
      }
    } catch (error) {
      console.error('Error getting devices:', error);
      alert('To configure devices, please allow access to camera and microphone.');
    } finally {
      setIsLoadingDevices(false);
    }
  };

  useEffect(() => {
    if (showDeviceSettings) getDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeviceSettings]);

  useEffect(() => {
    const savedCamera = localStorage.getItem('selectedCamera');
    const savedMicrophone = localStorage.getItem('selectedMicrophone');
    if (savedCamera) setSelectedCamera(savedCamera);
    if (savedMicrophone) setSelectedMicrophone(savedMicrophone);
  }, []);

  const tabs = [
    { id: 'personal-info', name: 'Personal info' },
    { id: 'description', name: 'Notifications' },
    { id: 'payments', name: 'Payments and plans' },
  ];

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const studentId = studentData?.id;
    if (!studentId) {
      alert('Unable to upload photo — student not identified.');
      return;
    }

    try {
      // Same pattern as the teacher's own photo upload
      // (ProfileSettings.jsx) — this previously only wrote a base64
      // data URL into localStorage, never actually saving to Supabase.
      const fileExt = file.name.split('.').pop();
      const fileName = `students/${studentId}/profile-photo-${Date.now()}.${fileExt}`;
      const photoUrl = await uploadFile(file, fileName, { upsert: true });

      setProfilePhoto(photoUrl);

      const { error: updateError } = await supabase
        .from('students')
        .update({ photo: photoUrl })
        .eq('id', studentId);

      if (updateError) {
        throw new Error(`Failed to save photo URL to database: ${updateError.message}`);
      }

      // Keep the localStorage cache in sync too — getCurrentStudent()
      // and the mobile nav avatar both still read from there first.
      saveStudentField('photo', photoUrl);
      showSavedToast();
    } catch (error) {
      console.error('Error uploading photo:', error);
      let errorMessage = 'Failed to upload photo. ';
      if (error.message?.includes('Bucket not found') || error.message?.includes('does not exist')) {
        errorMessage += 'Storage bucket is not configured. Please contact administrator.';
      } else if (error.message?.includes('Permission denied') || error.message?.includes('RLS')) {
        errorMessage += 'Permission denied. Please check your access rights.';
      } else {
        errorMessage += 'Please try again.';
      }
      alert(errorMessage);
    }
  };

  const handleEditPhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFieldChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
    
    const hasFieldChanges = value !== originalData[field];
    
    if (field === 'name') {
      setHasNameChanges(hasFieldChanges);
    } else if (field === 'phone') {
      setHasPhoneChanges(hasFieldChanges);
    }
  };

  const saveStudentField = (field, value) => {
    try {
      console.log('Saving field:', field, 'with value:', value);
      
      const allStudentDataRaw = localStorage.getItem('novas_students_data');
      console.log('Raw allStudentData from localStorage before save:', allStudentDataRaw);
      
      const allStudentData = JSON.parse(allStudentDataRaw || '{}');
      console.log('Parsed allStudentData before save:', allStudentData);
      console.log('All student data keys before save:', Object.keys(allStudentData));
      
      const studentSlug = studentData?.slug || studentData?.originalSlug || studentData?.id;
      
      if (studentSlug && allStudentData[studentSlug]) {
        console.log('Found student data for slug:', studentSlug);
        console.log('Student is registered:', allStudentData[studentSlug].isRegistered);
        console.log('Student userAccount exists:', !!allStudentData[studentSlug].userAccount);
        console.log('Student userAccount:', allStudentData[studentSlug].userAccount);
        
        if (field === 'phone') {
          // Если студент зарегистрирован, сохраняем телефон в userAccount.phone
          if (allStudentData[studentSlug].isRegistered && allStudentData[studentSlug].userAccount) {
            console.log('Saving phone to userAccount.phone');
            console.log('Before save - userAccount.phone:', allStudentData[studentSlug].userAccount.phone);
            allStudentData[studentSlug].userAccount.phone = value;
            console.log('After save - userAccount.phone:', allStudentData[studentSlug].userAccount.phone);
          } else {
            // Если не зарегистрирован, сохраняем в contactPhone
            console.log('Saving phone to contactPhone');
            console.log('Before save - contactPhone:', allStudentData[studentSlug].contactPhone);
            allStudentData[studentSlug].contactPhone = value;
            console.log('After save - contactPhone:', allStudentData[studentSlug].contactPhone);
          }
        } else {
          // Для других полей сохраняем как обычно
          console.log('Saving field as usual:', field);
          allStudentData[studentSlug] = {
            ...allStudentData[studentSlug],
            [field]: value
          };
        }
        localStorage.setItem('novas_students_data', JSON.stringify(allStudentData));
        console.log('Data saved successfully');
        console.log('Updated student data:', allStudentData[studentSlug]);
        window.dispatchEvent(new CustomEvent('studentDataUpdated'));
        const skipToastFields = ['timezone', 'photo', 'interface_language', 'native_language', 'country', 'telegram_notifications_enabled'];
        if (!skipToastFields.includes(field)) showSavedToast();
      } else {
        console.log('Student not found for slug:', studentSlug);
        console.log('Available slugs:', Object.keys(allStudentData));
        console.log('studentData prop:', studentData);
        console.log('studentData.slug:', studentData?.slug);
        console.log('studentData.originalSlug:', studentData?.originalSlug);
      }
    } catch (error) {
      console.error('Error saving student field:', error);
    }
  };

  const handleSaveName = () => {
    const nextName = (profileData.name || '').trim();
    if (!nextName) {
      // don't persist empty name; just revert UI to last saved value
      setProfileData(prev => ({ ...prev, name: originalData.name || '' }));
      setHasNameChanges(false);
      return;
    }
    saveStudentField('name', nextName);
    setProfileData(prev => ({ ...prev, name: nextName }));
    setOriginalData(prev => ({ ...prev, name: nextName }));
    setHasNameChanges(false);
  };

  const handleSavePhone = async () => {
    try {
      // Mirror ProfileSettings (teacher) — go straight to Supabase via
      // the auth session instead of reading slugs out of localStorage.
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('User not authenticated');

      // users.phone is the canonical place for a registered student's
      // contact phone (it's what userAccount.phone reads back).
      const { error: usersErr } = await supabase
        .from('users')
        .update({ phone: profileData.phone })
        .eq('id', authUser.id);
      if (usersErr) throw usersErr;

      // Keep the dashboard's localStorage caches in sync so banner /
      // homework widgets reflect the new phone immediately, without
      // waiting for a refresh.
      try {
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        currentUser.phone = profileData.phone;
        localStorage.setItem('currentUser', JSON.stringify(currentUser));
      } catch (_) {}
      saveStudentField('phone', profileData.phone);

      window.dispatchEvent(new CustomEvent('userDataUpdated'));
      window.dispatchEvent(new CustomEvent('studentDataUpdated'));

      setOriginalData(prev => ({ ...prev, phone: profileData.phone }));
      setHasPhoneChanges(false);
      showSavedToast();
    } catch (error) {
      console.error('Error saving phone:', error);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleSave = () => {
    saveStudentField('name', profileData.name);
    saveStudentField('phone', profileData.phone);
    
    setOriginalData({...profileData});
    setHasNameChanges(false);
    setHasPhoneChanges(false);
  };

  const handleChangePassword = () => {
    setShowPasswordFields(true);
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const [passwordError, setPasswordError] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  const handleSavePassword = async () => {
    setPasswordError('');
    if (!passwordData.oldPassword || !passwordData.newPassword) {
      setPasswordError('Fill in both your current and new password.');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters.');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    setPasswordSaving(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser?.email) throw new Error('Not authenticated');

      // Verify the current password by re-authenticating. Supabase
      // updateUser({password}) doesn't ask for the old one itself, so
      // gate the change manually.
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: authUser.email,
        password: passwordData.oldPassword,
      });
      if (signInErr) {
        setPasswordError('Current password is incorrect.');
        return;
      }

      const { error: updErr } = await supabase.auth.updateUser({
        password: passwordData.newPassword,
      });
      if (updErr) throw updErr;

      // Mirror to legacy users.password_hash so old code paths that
      // still read it stay in sync (it's NOT NULL on the table).
      try {
        const passwordHash = await bcrypt.hash(passwordData.newPassword, 10);
        await supabase
          .from('users')
          .update({ password_hash: passwordHash })
          .eq('id', authUser.id);
      } catch (mirrorErr) {
        console.warn('users.password_hash mirror failed:', mirrorErr?.message);
      }

      setShowPasswordFields(false);
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordError('');
      showSavedToast();
    } catch (e) {
      console.error('Password change error:', e);
      setPasswordError(e?.message || 'Could not change password. Try again.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleCancelPassword = () => {
    setShowPasswordFields(false);
    setPasswordData({
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
  };

  const renderContent = () => {
    // Payments has its own local vars below (balance, sorted history) that
    // don't belong in the switch below, so it's handled as an early return.
    if (activeTab === 'payments') {
      // Reconciled balance (via usePaymentBalance, same ledger HomeStudent
      // and the teacher's StudentProfile use) — reading studentData.payments
      // directly here could show a stale figure and a history list missing
      // auto-charges for lessons completed since the last reconciliation.
      const payments = reconciledPayments;
      const balance = paymentBalance;
      const currency = paymentCurrency;
      const priceRaw = studentData?.price || '';
      const priceMatch = priceRaw.match(/(\d+(?:\.\d+)?)/);
      const pricePerHour = priceMatch ? parseFloat(priceMatch[1]) : 0;
      const lessonsCount = pricePerHour > 0 ? Math.round(Math.abs(balance) / pricePerHour) : 0;
      const lessonsLabel = lessonsCount > 0
        ? balance > 0
          ? `${lessonsCount} lesson${lessonsCount !== 1 ? 's' : ''} paid ahead`
          : `${lessonsCount} lesson${lessonsCount !== 1 ? 's' : ''} unpaid`
        : null;
      const sortedPayments = [...payments].sort((a, b) =>
        new Date(b.date || 0) - new Date(a.date || 0)
      );
      const formatDate = (iso) => {
        if (!iso) return '';
        try {
          return new Date(iso).toLocaleDateString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric',
          });
        } catch { return ''; }
      };
      const formatAmount = (amt) => {
        const n = Number(amt) || 0;
        const sign = n >= 0 ? '+' : '−';
        return `${sign}${currency}${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
      };
      return (
        <div className="student-profile-content">
          <div className="student-profile-header">
            <h1>Payments and plans</h1>
            <p className="student-profile-header-subtitle">Your balance and billing history.</p>
          </div>
          <div className="student-profile-main-content">
            <div className="student-profile-fields-section">
              <div className={`student-balance-card ${balance >= 0 ? 'positive' : 'negative'}`}>
                <div className="student-balance-label">
                  {balance >= 0 ? 'Balance' : 'Debt'}
                </div>
                <div className="student-balance-amount">
                  {balance < 0 ? '−' : ''}{currency}{Math.abs(balance).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                </div>
                {lessonsLabel && (
                  <div className="student-balance-meta">{lessonsLabel}</div>
                )}
                {pricePerHour > 0 && (
                  <div className="student-balance-rate">
                    Lesson rate: {currency}{pricePerHour}/h
                  </div>
                )}
              </div>
              <div className="student-payments-history student-settings-card">
                <div className="student-payments-history-header">History</div>
                {sortedPayments.length === 0 ? (
                  <div className="student-payments-empty">
                    No transactions yet. Your teacher will record deposits and lesson charges here.
                  </div>
                ) : (
                  <ul className="student-payments-list">
                    {sortedPayments.map((p) => (
                      <li key={p.id} className={`student-payments-item ${p.amount >= 0 ? 'positive' : 'negative'}`}>
                        <div className="student-payments-item-main">
                          <div className="student-payments-item-note">
                            {p.note || (p.amount >= 0 ? 'Deposit' : 'Charge')}
                          </div>
                          <div className="student-payments-item-date">{formatDate(p.date)}</div>
                        </div>
                        <div className="student-payments-item-amount">{formatAmount(p.amount)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'personal-info':
        return (
          <div className="student-profile-content">
            <div className="student-profile-header">
              <h1>Personal profile</h1>
              <p className="student-profile-header-subtitle">Manage your personal details and preferences.</p>
            </div>

            <div className="student-profile-right">
                <div className="student-settings-card">
                <h2 className="student-settings-card-title">Basic info</h2>

                <div className="student-settings-row-avatar">
                  <div className="student-profile-photo-circle" onClick={handleEditPhotoClick}>
                    {profilePhoto ? (
                      <img src={profilePhoto} alt="Profile" className="student-profile-photo-img" />
                    ) : (
                      <span className="student-profile-initial">
                        {(profileData.name || 'Student').split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="student-settings-row-avatar-field">
                    <label>Name</label>
                    <div className="student-profile-input-with-save">
                      <input
                        type="text"
                        placeholder="Student Name"
                        value={profileData.name || ''}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                        className="student-profile-input"
                      />
                      {hasNameChanges && (
                        <button className="student-profile-save-btn" onClick={handleSaveName}>
                          Save
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <button className="student-profile-edit-photo-btn" onClick={handleEditPhotoClick}>Change photo</button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  style={{ display: 'none' }}
                />

                <div className="student-profile-field">
                  <label>Email</label>
                  <div className="student-profile-input-with-save">
                    <input
                      type="email"
                      value={profileData.email}
                      className="student-profile-input student-profile-input-disabled"
                      disabled
                      readOnly
                    />
                  </div>
                </div>

                <div className="student-profile-field">
                  <label>Password</label>
                  {!showPasswordFields ? (
                    <div className="student-profile-password-field">
                      <input type="password" value="••••••••" className="student-profile-input student-profile-input-disabled" disabled readOnly />
                      <button className="student-profile-change-btn" onClick={handleChangePassword}>Change</button>
                    </div>
                  ) : (
                    <div className="student-profile-password-change-fields">
                      <input
                        type="password"
                        placeholder="Old password"
                        value={passwordData.oldPassword}
                        onChange={(e) => handlePasswordChange('oldPassword', e.target.value)}
                        className="student-profile-password-change-input"
                      />
                      <input
                        type="password"
                        placeholder="New password"
                        value={passwordData.newPassword}
                        onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                        className={`student-profile-password-change-input ${!passwordData.oldPassword ? 'student-profile-password-input-disabled' : ''}`}
                        disabled={!passwordData.oldPassword}
                      />
                      <input
                        type="password"
                        placeholder="Confirm new password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                        className={`student-profile-password-change-input ${!passwordData.oldPassword ? 'student-profile-password-input-disabled' : ''}`}
                        disabled={!passwordData.oldPassword}
                      />
                      {passwordError && (
                        <div style={{
                          marginTop: 8,
                          padding: '8px 12px',
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: 8,
                          color: '#b91c1c',
                          fontSize: 13,
                          lineHeight: 1.4,
                        }}>
                          {passwordError}
                        </div>
                      )}
                      <div className="student-profile-password-change-buttons">
                        <button className="student-profile-password-cancel-btn" onClick={handleCancelPassword} disabled={passwordSaving}>
                          Cancel
                        </button>
                        <button className="student-profile-password-save-btn" onClick={handleSavePassword} disabled={passwordSaving}>
                          {passwordSaving ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="student-profile-field">
                  <label>Phone number</label>
                  <div className="student-profile-input-with-save">
                    <input 
                      type="tel" 
                      placeholder="Your phone number" 
                      value={profileData.phone}
                      onChange={(e) => handleFieldChange('phone', e.target.value)}
                      className="student-profile-input"
                    />
                    {hasPhoneChanges && (
                      <button className="student-profile-save-btn" onClick={handleSavePhone}>
                        Save
                      </button>
                    )}
                  </div>
                </div>
                </div>

                <div className="student-settings-card">
                <h2 className="student-settings-card-title">Region &amp; language</h2>
                <div className="student-profile-field">
                  <label>Interface language</label>
                  <div className="student-profile-language-dropdown">
                    <div className="student-profile-language-selected" onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}>
                      <div className="student-profile-language-info">
                        <span className="student-profile-flag">{selectedLanguage.flag}</span>
                        <span className="student-profile-language-name">{selectedLanguage.name}</span>
                      </div>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    {showLanguageDropdown && (
                      <div className="student-profile-language-dropdown-content">
                        <input 
                          type="text" 
                          placeholder="Search language..." 
                          className="student-profile-language-search"
                          value={languageSearch}
                          onChange={(e) => setLanguageSearch(e.target.value)}
                        />
                        <div className="student-profile-language-options">
                          {filteredLanguages.map(lang => (
                            <div 
                              key={lang.value} 
                              className="student-profile-language-option"
                              onClick={() => {
                                setSelectedLanguage(lang);
                                setShowLanguageDropdown(false);
                                setLanguageSearch('');
                                saveStudentSupabaseField('interface_language', lang.value);
                              }}
                            >
                              <span className="student-profile-flag">{lang.flag}</span>
                              <span className="student-profile-language-name">{lang.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: '#6c757d' }}>
                    More languages coming soon — the interface is English-only for now.
                  </p>
                </div>

                <div className="student-profile-field">
                  <label>Native language</label>
                  <div className="student-profile-language-dropdown">
                    <div className="student-profile-language-selected" onClick={() => setShowNativeLanguageDropdown(!showNativeLanguageDropdown)}>
                      <div className="student-profile-language-info">
                        <span className="student-profile-flag">{selectedNativeLanguage.flag}</span>
                        <span className="student-profile-language-name">{selectedNativeLanguage.name}</span>
                      </div>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    {showNativeLanguageDropdown && (
                      <div className="student-profile-language-dropdown-content">
                        <input 
                          type="text" 
                          placeholder="Search language..." 
                          className="student-profile-language-search"
                          value={nativeLanguageSearch}
                          onChange={(e) => setNativeLanguageSearch(e.target.value)}
                        />
                        <div className="student-profile-language-options">
                          {filteredNativeLanguages.map(lang => (
                            <div 
                              key={lang.value} 
                              className="student-profile-language-option"
                              onClick={() => {
                                setSelectedNativeLanguage(lang);
                                setShowNativeLanguageDropdown(false);
                                setNativeLanguageSearch('');
                                saveStudentSupabaseField('native_language', lang.value);
                              }}
                            >
                              <span className="student-profile-flag">{lang.flag}</span>
                              <span className="student-profile-language-name">{lang.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="student-profile-field">
                  <label>Country</label>
                  <div className="student-profile-language-dropdown">
                    <div className="student-profile-language-selected" onClick={() => setShowCountryDropdown(!showCountryDropdown)}>
                      <div className="student-profile-language-info">
                        <span className="student-profile-flag">{selectedCountry.flag}</span>
                        <span className="student-profile-language-name">{selectedCountry.name}</span>
                      </div>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    {showCountryDropdown && (
                      <div className="student-profile-language-dropdown-content">
                        <input 
                          type="text" 
                          placeholder="Search country..." 
                          className="student-profile-language-search"
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                        />
                        <div className="student-profile-language-options">
                          {filteredCountries.map(country => (
                            <div 
                              key={country.value} 
                              className="student-profile-language-option"
                              onClick={() => {
                                setSelectedCountry(country);
                                setShowCountryDropdown(false);
                                setCountrySearch('');
                                saveStudentSupabaseField('country', country.value);
                              }}
                            >
                              <span className="student-profile-flag">{country.flag}</span>
                              <span className="student-profile-language-name">{country.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="student-profile-field">
                  <label>Timezone</label>
                  <div className="student-profile-language-dropdown">
                    <div className="student-profile-language-selected" onClick={() => setShowTimezoneDropdown(!showTimezoneDropdown)}>
                      <div className="student-profile-language-info">
                        <span className="student-profile-language-name">{selectedTimezone.name}</span>
                      </div>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    {showTimezoneDropdown && (
                      <div className="student-profile-timezone-dropdown-content">
                        <input 
                          type="text" 
                          placeholder="Search timezone..." 
                          className="student-profile-language-search"
                          value={timezoneSearch}
                          onChange={(e) => setTimezoneSearch(e.target.value)}
                        />
                        <div className="student-profile-language-options">
                          {filteredTimezones.map(timezone => (
                            <div 
                              key={timezone.value} 
                              className="student-profile-language-option"
                              onClick={() => {
                                setSelectedTimezone(timezone);
                                setShowTimezoneDropdown(false);
                                setTimezoneSearch('');
                                saveStudentTimezone(timezone.value);
                              }}
                            >
                              <span className="student-profile-language-name">{timezone.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                </div>

                <div className="student-settings-card">
                <h2 className="student-settings-card-title">Video conferencing</h2>
                <div className="student-profile-field">
                  <label>Check your camera and microphone</label>
                  <button
                    className="student-profile-video-check-btn"
                    onClick={openDeviceSettings}
                  >
                    Check
                  </button>
                </div>
                </div>
            </div>
          </div>
        );
      case 'description':
        return (
          <div className="student-profile-content">
            <div className="student-profile-header">
              <h1>Notifications</h1>
              <p className="student-profile-header-subtitle">Choose what you get notified about.</p>
            </div>

            <div className="student-profile-main-content">
              <div className="student-profile-right">
                <div className="student-settings-card">
                  <h2 className="student-settings-card-title">Telegram</h2>
                  <div className="student-profile-notifications-container">
                    <div className="student-profile-notification-category">
                      <div className="student-profile-notification-item">
                        <div className="student-profile-notification-label">
                          <span>Forward messages from your teacher to Telegram</span>
                        </div>
                        <label className="student-profile-toggle">
                          <input
                            type="checkbox"
                            checked={telegramEnabled}
                            disabled={telegramLoading}
                            onChange={toggleTelegramNotifications}
                          />
                          <span className="student-profile-slider"></span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="student-profile-settings-page">
      <div className="student-profile-settings-container">
        {/* Left Navigation */}
        <div className="student-profile-nav">
          <div className="student-profile-nav-header">
            <h1>Profile Settings</h1>
            {/* Mobile only — desktop's sign-out sits below the tab list
                (.student-profile-signout-btn-bottom) instead. */}
            <button
              type="button"
              className="student-profile-signout-btn student-profile-signout-btn-header"
              onClick={() => setShowSignOutConfirm(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span>Sign out</span>
            </button>
          </div>

          <div className="student-profile-nav-tabs">
            {tabs.map(tab => (
              <div
                key={tab.id}
                className={`student-profile-nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <NavIcon id={tab.id} />
                <span>{tab.name}</span>
              </div>
            ))}
          </div>

          {/* Desktop only — mobile uses .student-profile-signout-btn-header instead. */}
          <button
            type="button"
            className="student-profile-signout-btn student-profile-signout-btn-bottom"
            onClick={() => setShowSignOutConfirm(true)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span>Sign out</span>
          </button>
        </div>

        {/* Right Content */}
        <div className="student-profile-main">
          {renderContent()}
        </div>
      </div>

      {showDeviceSettings && (
        <div className="device-settings-modal-overlay">
          <div className="device-settings-modal">
            <div className="device-settings-header">
              <h2>Video Settings</h2>
              <button type="button" className="close-btn" onClick={closeDeviceSettings} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="device-settings-content">
              {isLoadingDevices ? (
                <div className="device-loading">
                  <div className="loading-spinner" />
                  <p>Loading devices...</p>
                </div>
              ) : (
                <>
                  <div className="device-section">
                    <div className="device-label">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      Camera
                    </div>
                    <select
                      className="device-select"
                      value={selectedCamera}
                      onChange={(e) => { setSelectedCamera(e.target.value); startCamera(e.target.value); }}
                    >
                      {availableCameras.map(camera => (
                        <option key={camera.deviceId} value={camera.deviceId}>
                          {camera.label || `Camera ${camera.deviceId.slice(0, 8)}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="device-section">
                    <div className="camera-preview-container">
                      {cameraError ? (
                        <div className="camera-error">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                            <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/>
                            <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                          <p>Camera error: {cameraError}</p>
                        </div>
                      ) : (
                        <video ref={videoRef} className="camera-preview" autoPlay playsInline muted />
                      )}
                    </div>
                  </div>
                  <div className="device-section">
                    <div className="device-label">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="12" y1="19" x2="12" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="8" y1="23" x2="16" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Microphone
                    </div>
                    <select
                      className="device-select"
                      value={selectedMicrophone}
                      onChange={(e) => { setSelectedMicrophone(e.target.value); startMicrophone(e.target.value); }}
                    >
                      {availableMicrophones.map(mic => (
                        <option key={mic.deviceId} value={mic.deviceId}>
                          {mic.label || `Microphone ${mic.deviceId.slice(0, 8)}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="device-section">
                    <div className="microphone-level-container">
                      <div className="microphone-level-label">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Sound level: {microphoneLevel}%
                      </div>
                      <div className="microphone-level-bar">
                        <div className="microphone-level-fill" style={{ width: `${microphoneLevel}%` }} />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="device-settings-footer">
              <button type="button" className="cancel-btn" onClick={closeDeviceSettings}>Cancel</button>
              <button type="button" className="save-btn" onClick={saveDeviceSettings}>Save</button>
            </div>
          </div>
        </div>
      )}

      {savedToast && (
        <div className="student-profile-saved-toast" role="status" aria-live="polite">
          <div className="student-profile-saved-toast-inner">
            <span className="student-profile-saved-toast-icon" aria-hidden>✓</span>
            <p className="student-profile-saved-toast-message">Changes are saved</p>
            <button type="button" className="student-profile-saved-toast-close" onClick={() => setSavedToast(false)} aria-label="Close">×</button>
          </div>
        </div>
      )}

      <ConfirmSignOutModal
        isOpen={showSignOutConfirm}
        onCancel={() => setShowSignOutConfirm(false)}
        onConfirm={handleSignOut}
      />
    </div>
  );
};

export default StudentProfileSettings;
